import { useState } from "react";
import {
  DOCTOR_MESSAGE_TYPES,
  MEETING_NOTICE_COPY,
  type DoctorMessageType,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { sendDoctorNotice, UnauthorizedError } from "@/lib/clinician-api";
import {
  answerRequest,
  EMPTY_ANSWER_DRAFT,
  type AnswerDraft,
} from "@/lib/medical-question";
import {
  meetingMessageRequest,
  type MeetingMessageDraft,
} from "@/lib/meeting-notice";
import { MedicalNoticeDraftFields } from "./MedicalNoticeDraftFields";
import { MeetingMessageFields } from "./MeetingMessageFields";
import { useReadOnly } from "./SupportView";

// TES-1147 (Emelie 2026-10-07) — "Besked till patienten": how the doctor
// reaches a patient when there is no question to answer. There is no free
// doctor chat in the MVP; a notice is published under Din vård.
//
// TES-1332 (Emelie 2026-10-13) — in one of two modes, "Besked – inget svar
// behövs" or "Besked med fråga – patienten ska svara"
// (MedicalNoticeDraftFields). The generic task is gone from this flow.
// `followUpOf` makes it the queue's "Skicka nytt besked": the answered
// question it follows up is marked handled with the new notice.
//
// Doctor only, outside the admin's read-only view: the notice is signed by
// the doctor who writes it (the server holds the same rule).
//
// TES-1393 (2.1 S5; 09C 2026-10-13) — first the type of message: 09C's
// "Medicinskt besked" (the notice above), "Om ditt vårdmöte" or "Förslag om
// extra vårdmöte" (MeetingMessageFields), which are read under Din vård →
// "Vårdmöten". "Skicka nytt besked" is always a medical notice.
//
// TES-1637 (Emelie 2026-10-28) — `monthlyCase` makes it "Ställ en
// följdfråga" from the monthly case: always "Besked med fråga", tied to the
// case (the prescription cycle), which reads "Inväntar patientsvar" until
// she answers in "Meddelanden → Läkare".
//
// The staff-facing strings here are proposed copy, except the decision's
// mode labels, the message type names and "Skicka nytt besked".
export function SendDoctorNotice({
  patientId,
  onUnauthorized,
  initialBody = "",
  startOpen = false,
  followUpOf,
  monthlyCase,
  onDone,
  onSent,
}: {
  patientId: string;
  onUnauthorized: () => void;
  /** Pre-filled text, e.g. TES-999's "be patienten rätta ett värde". */
  initialBody?: string;
  startOpen?: boolean;
  /** TES-1332 — the answered question this notice follows up. */
  followUpOf?: { taskId: string };
  /** TES-1637 — a follow-up question asked in this monthly case. */
  monthlyCase?: { cycleId: string };
  /** Called after a publish or a cancel, when the caller owns the form. */
  onDone?: () => void;
  /** TES-1149 — called after a publish, e.g. to reload the questions. */
  onSent?: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(startOpen);
  // A monthly case's follow-up is a question, so it starts in that mode.
  const emptyDraft: AnswerDraft = monthlyCase
    ? { ...EMPTY_ANSWER_DRAFT, mode: "question" }
    : EMPTY_ANSWER_DRAFT;
  const [draft, setDraft] = useState<AnswerDraft>({
    ...emptyDraft,
    body: initialBody,
  });
  const [type, setType] = useState<DoctorMessageType>("doctor_notice");
  const [meeting, setMeeting] = useState<Omit<MeetingMessageDraft, "kind">>({
    body: "",
    cta: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (claims?.role !== "doctor" || readOnly) return null;

  const label = monthlyCase
    ? "Ställ en följdfråga"
    : followUpOf
      ? "Skicka nytt besked"
      : "Skicka besked";

  const notice = answerRequest(draft);
  const request = monthlyCase
    ? notice?.question
      ? { ...notice, monthlyCase }
      : null
    : type === "doctor_notice"
      ? notice && { ...notice, ...(followUpOf ? { followUpOf } : {}) }
      : meetingMessageRequest({ kind: type, ...meeting });

  async function publish() {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      await sendDoctorNotice(patientId, request);
      onSent?.();
      setSent(true);
      setOpen(false);
      setDraft(emptyDraft);
      setType("doctor_notice");
      setMeeting({ body: "", cta: "" });
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="flex flex-col gap-xs text-sm">
        {sent ? (
          <p className="text-xs text-textSecondary" role="status">
            Beskedet är publicerat under Din vård.
          </p>
        ) : null}
        {onDone && sent ? (
          <button
            type="button"
            onClick={onDone}
            className="self-start text-xs underline"
          >
            Stäng
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setOpen(true);
            }}
            className="self-start rounded-md border border-brand px-md py-xs text-sm text-brand hover:bg-brand/5"
          >
            {label}
          </button>
        )}
      </div>
    );
  }

  return (
    <form
      aria-label={label}
      onSubmit={(e) => {
        e.preventDefault();
        void publish();
      }}
      className="flex flex-col gap-sm rounded-md border border-brand/40 bg-brand/5 p-sm text-sm"
    >
      {type === "doctor_notice" ? (
        <p className="text-xs text-textSecondary">
          Beskedet publiceras som ett medicinskt besked under Din vård, signerat
          av dig, och patienten får en notifiering. Det finns ingen läkarchatt:
          behöver du ett svar, välj Besked med fråga.
        </p>
      ) : null}
      {followUpOf || monthlyCase ? null : (
        <fieldset className="flex flex-col gap-xs">
          <legend className="mb-xs">Typ av meddelande</legend>
          {DOCTOR_MESSAGE_TYPES.map((t) => (
            <label key={t} className="flex items-center gap-xs">
              <input
                type="radio"
                name="doctor-message-type"
                value={t}
                checked={type === t}
                onChange={() => setType(t)}
              />
              {MEETING_NOTICE_COPY.typeLabel[t]}
            </label>
          ))}
        </fieldset>
      )}
      {type === "doctor_notice" ? (
        <MedicalNoticeDraftFields
          draft={draft}
          onChange={setDraft}
          name={
            followUpOf
              ? `notice-mode-${followUpOf.taskId}`
              : monthlyCase
                ? `notice-mode-cycle-${monthlyCase.cycleId}`
                : "notice-mode"
          }
        />
      ) : (
        <MeetingMessageFields
          draft={{ kind: type, ...meeting }}
          onChange={({ body, cta }) => setMeeting({ body, cta })}
        />
      )}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex justify-end gap-sm">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            onDone?.();
          }}
          className="rounded-md border border-surfaceMuted px-md py-xs text-textSecondary hover:text-textPrimary"
        >
          Avbryt
        </button>
        <button
          type="submit"
          disabled={busy || !request}
          className="rounded-md bg-brand px-md py-xs text-textOnBrand hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Publicerar…" : "Publicera beskedet"}
        </button>
      </div>
    </form>
  );
}
